
import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Card, CardHeader, Chip, Grid2, IconButton, InputAdornment, Menu, Skeleton, Stack, TextField, Typography, useTheme } from "@mui/material";
import PhysicalProgress from "./PhysicalProgress";
import FinancialProgress from "./FinancialProgress";
import CostPerformanceIndicator from "./CostPerformanceIndicator";
import SchedulePerformanceIndicator from "./SchedulePerformanceIndicator";
import { useNavigate } from "react-router-dom";
import { DEFAULT_PROJECT_DASHBOARD } from "config";
import MonthlyHighlights from "./MonthlyHighlights";
import MonteCarloSimulationReport from "./MonteCarloSimulationReport";
import { useGetProgramSummaryQuery } from "store/slices/apis/programDashboardSlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import moment from 'moment';
import { ReactComponent as TimeOutlineBlack } from "assets/PwC_Funct_Icons_Time_Outline_Black_RGB.svg";
import { ReactComponent as LocationIcon } from "assets/PwC_Funct_Icons_Location_Fill_Black_RGB.svg";
import { ReactComponent as SearchIcon } from "assets/PwC_Funct_Icons_Search_Outline_Black_RGB.svg";
import { ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg";
import { ReactComponent as BUildingIcon } from "assets/PwC_Funct_Icons_Building_Outline_Black_RGB.svg"
import { useRedirectFromProjects } from "hooks/useRedirectFromProjects";
import { useLazyGetProjectPermissionQuery } from "store/slices/apis/projectPermissionSlice";
import { useAuth } from "context/AuthContext";

const ProgramDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { data: projectDetails, isLoading: detailsLoading } = useGetProgramSummaryQuery();
    const [projectPermissionFun, projectPermissionRes] = useLazyGetProjectPermissionQuery();
    const { data: AllProjects = [], isLoading } = useGetAllProjectsQuery();
    const [input, setInput] = useState('');
    const handleClick = useRedirectFromProjects();
    const { setUser, user } = useAuth();
    const summary = projectDetails?.data

    const getSortedProjects = () => {
        return [...(AllProjects?.data || [])].sort(
            (a, b) => new Date(b.createddate) - new Date(a.createddate)
        );
    };

    const handleGetProjectPermission = async (projectVal) => {
        await projectPermissionFun({ projectId: projectVal?.project_id })
        // handleClick(projectVal)
    }

    useEffect(() => {
        if (projectPermissionRes?.isSuccess) {
            if (projectPermissionRes?.data?.status == 'success') {
                setUser({
                    ...user,
                    permission: projectPermissionRes?.data?.result?.modules
                });
                handleClick(projectPermissionRes?.originalArgs?.projectId, projectPermissionRes?.data?.result?.modules);
            }
        }
    }, [projectPermissionRes?.isSuccess])

    const trimmedInput = input?.trim().toLowerCase();
    const filteredProjects = getSortedProjects().filter(project =>
        project.project_name.toLowerCase().includes(trimmedInput) ||
        project.project_manager_name.toLowerCase().includes(trimmedInput) ||
        project.project_manager_email.toLowerCase().includes(trimmedInput),
    );

    const programsCards = [
        {
            title: summary?.totalProjects !== undefined
                ? `${summary.totalProjects} project${summary.totalProjects > 1 ? "s" : ""}`
                : "-",
            subtitle: "Ongoing Projects",
        },
        {
            title: summary?.totalStates !== undefined
                ? `${summary.totalStates} state${summary.totalStates > 1 ? "s" : ""}`
                : "-",
            subtitle: "Project Locations",
        },
        {
            title: summary?.totalBudget ? `₹ ${summary.totalBudget} Cr` : "₹ 0",
            subtitle: "Total Budget Allocated",
        },
        {
            title: summary?.fundRequirement ? `₹ ${summary.fundRequirement} Cr` : "₹ 0",
            subtitle: "Fund Requirement in Next 6 Months",
        },
        {
            title: summary?.claimValue ? `₹ ${summary.claimValue} Cr` : "₹ 0",
            subtitle: "Value of Claims Submitted",
        }
    ];

    return (
        <>
            <Grid2 container spacing={1} sx={{ mt: 5 }}>
                <Grid2 size={12}>
                    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: '700', color: '#252525', mb: 3, lineHeight: "32px", letterSpacing: '-0.4px' }}>
                        Program Dashboard
                    </Typography>
                    <Typography variant="h5" sx={{ fontSize: '20px', fontWeight: '500', color: '#000000', mb: 1, lineHeight: "100%", letterSpacing: '-0.4px' }}>
                        Status overview of all Projects
                    </Typography>
                    <Typography variant="h5" sx={{ fontSize: '18px', fontWeight: '400', color: '#182A4E80', mb: 2, lineHeight: "100%", letterSpacing: '-0.4px' }}>
                        Get the snapshot of the status of all your currently running projects. You can also navigate to the individual project dashboards
                    </Typography>
                </Grid2>
                {programsCards?.map((val) => {
                    return <Grid2 size={{ xs: 12, md: 2.4 }}>
                        <Card sx={{ boxShadow: "none", border: '1px solid #25252517', borderRadius: '3px' }}>
                            <CardHeader
                                avatar={
                                    <Avatar sx={{
                                        bgcolor: "#9ED3CC", borderRadius: "initial", '& svg': {
                                            display: 'none', // hide any default SVG icon

                                        }
                                    }} aria-label="recipe">
                                    </Avatar>
                                }
                                sx={{
                                    '& .MuiCardHeader-title': {
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        lineHeight: "100%",
                                        letterSpacing: '-0.4px',
                                        color: '#252525',
                                        marginBottom: 1
                                    },
                                    '& .MuiCardHeader-subheader': {
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        lineHeight: "100%",
                                        letterSpacing: '-0.4px',
                                        color: '#25252580'
                                    }
                                }}
                                title={val?.title}
                                subheader={val?.subtitle}
                            />
                        </Card>
                    </Grid2>
                })}
                <Grid2 size={12}>
                    <Box sx={{ gap: 2, display: 'flex', alignItems: 'center', mt: 4 }}>
                        <TextField
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Search project with Project Name or Project Lead"
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {/* <IoIosSearch style={{ fontSize: "16px" }} /> */}
                                        <SearchIcon style={{ height: "20px", width: "20px", fill: "#808080" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: input ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setInput('')}
                                            edge="end" size="small" >
                                            {/* <ClearIcon style={{ fontSize: "16px" }} /> */}
                                            <CrossIcon style={{ fontSize: "20px", width: "20px", height: "20px", fill: "#808080 " }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                style: {
                                    fontSize: "16px",
                                    lineHeight: "24px",
                                    fontWeight: 400
                                },
                            }}
                            sx={{
                                minWidth: { xs: "100%", sm: "510px" },
                                height: "48px",
                                maxWidth: "500px",
                                background: "#fff",
                                "& .MuiInputBase-root": {
                                    height: "48px",
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "16px",
                                    lineHeight: "20px",
                                    fontWeight: "400",
                                    color: "#696969",
                                    opacity: 1,
                                },
                                "& .MuiInputBase-input::placeholder": {
                                    fontSize: { xs: "12px", sm: "16px" }, // 12px on xs, 13px on sm+
                                    lineHeight: "20px",
                                    fontWeight: "400",
                                    color: "#696969",
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Grid2>
                {isLoading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                        <Grid2 size={12} key={index}>
                            <Box sx={{ p: 2, borderRadius: '3px', bgcolor: '#fff', borderLeft: '6px solid #E0E0E0' }}>
                                <Stack spacing={2}>
                                    <Skeleton variant="rectangular" height={40} width="60%" />
                                    <Skeleton variant="text" height={20} width="40%" />
                                    <Skeleton variant="text" height={20} width="80%" />
                                    <Stack direction="row" spacing={2}>
                                        <Skeleton variant="rectangular" width="30%" height={80} />
                                        <Skeleton variant="rectangular" width="30%" height={80} />
                                        <Skeleton variant="rectangular" width="30%" height={80} />
                                    </Stack>
                                </Stack>
                            </Box>
                        </Grid2>
                    ))
                ) : filteredProjects?.length > 0 ? (
                    filteredProjects.map((item) => {
                        const projectCards = [
                            {
                                title: item?.start_date ? moment(item?.start_date).format("Do MMMM, YYYY") : "-",
                                subtitle: 'Project Start Date',
                            },
                            {
                                title: item?.end_date ? moment(item?.end_date).format("Do MMMM, YYYY") : "-",
                                subtitle: 'Project Completion Date',
                            },
                        ];
                        const today = moment();
                        const endDate = moment(item?.end_date);
                        const delayDays = today.isAfter(endDate) ? today.diff(endDate, 'days') : 0;
                        return (
                            <Grid2 size={12}>
                                <Box sx={{ p: 2, borderRadius: '3px', bgcolor: '#fff', borderLeft: { xs: 'none', sm: '6px solid #0496FF' } }}>
                                    <Grid2 container spacing={1}>
                                        <Grid2 size={{ xs: 12, md: 5 }}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <BUildingIcon
                                                    style={{ width: 45, height: 45 }}
                                                />
                                                <Box sx={{ ml: '8px !important' }}>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'start', sm: 'center' }} spacing={1}>
                                                        <Typography onClick={async () => {
                                                            // handleGetProjectPermission(item);
                                                            handleClick(item?.project_id);
                                                        }} sx={{
                                                            fontSize: '18px',
                                                            fontWeight: 700,
                                                            lineHeight: "100%",
                                                            letterSpacing: '-0.4px',
                                                            color: '#252525',
                                                            cursor: 'pointer'
                                                        }}>
                                                            {item?.project_name}
                                                        </Typography>
                                                        {delayDays > 0 && (
                                                            <Chip
                                                                avatar={
                                                                    <Avatar sx={{
                                                                        height: { xs: '10px !important', sm: '18px !important' },
                                                                        width: { xs: '10px !important', sm: '18px !important' },
                                                                        backgroundColor: '#CB402D',
                                                                        '& svg': {
                                                                            display: 'none',
                                                                        }
                                                                    }}></Avatar>
                                                                }
                                                                label={`Delayed by ${delayDays} day${delayDays > 1 ? 's' : ''}`}
                                                                sx={{
                                                                    marginLeft: { xs: '0px !important', sm: '20px!important' },
                                                                    marginTop: { xs: '0px !important' },
                                                                    background: 'none',
                                                                    color: '#182A4E',
                                                                    fontWeight: 500,
                                                                    fontSize: '14px'
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Stack direction="row" alignItems="center" spacing={0} mt={0.5}>
                                                        <LocationIcon
                                                            style={{
                                                                width: "20px",
                                                                height: "20px",
                                                                fill: '#182A4E'
                                                            }}
                                                        />
                                                        <Typography variant="caption"
                                                            sx={{
                                                                fontSize: '13px',
                                                                fontWeight: 500,
                                                                lineHeight: "100%",
                                                                letterSpacing: '-0.4px',
                                                                color: '#252525c4'
                                                            }}
                                                        >
                                                            Located at
                                                            {item?.address_line_1 ? ` ${item.address_line_1}` : ''}
                                                            {item?.address_line_2 ? `, ${item.address_line_2}` : ''}
                                                            {item?.city ? `, ${item.city}` : ''}
                                                            {item?.state ? `, ${item.state}` : ''}
                                                            {item?.zipcode ? `, ${item.zipcode}` : ''}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 7 }}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                                {projectCards.map((val, idx) => (
                                                    <Grid2 key={idx} size={{ xs: 12, md: 4, sm: 12 }}>
                                                        <Card sx={{ boxShadow: "none", borderRadius: '4px', bgcolor: '#F3F3F3' }}>
                                                            <CardHeader
                                                                avatar={
                                                                    <Avatar sx={{ bgcolor: '#cfd3dd' }} aria-label="recipe">
                                                                        {/* <MdOutlineWatchLater style={{ color: '#474747' }} /> */}
                                                                        <TimeOutlineBlack style={{ height: "25", width: "25px", fill: "#474747", stroke: "#474747", strokeWidth: 2 }} />
                                                                    </Avatar>
                                                                }
                                                                sx={{
                                                                    padding: '10px',
                                                                    marginRight: '10px',
                                                                    '& .MuiCardHeader-title': {
                                                                        fontSize: '16px',
                                                                        fontWeight: 600,
                                                                        lineHeight: "24px",
                                                                        letterSpacing: '-0.4px',
                                                                        color: '#474747',
                                                                    },
                                                                    '& .MuiCardHeader-subheader': {
                                                                        fontSize: '14px',
                                                                        fontWeight: 400,
                                                                        lineHeight: "24px",
                                                                        letterSpacing: '-0.4px',
                                                                        color: '#696969',
                                                                    }
                                                                }}
                                                                title={val?.title}
                                                                subheader={val?.subtitle}
                                                            />
                                                        </Card>
                                                    </Grid2>
                                                ))}
                                                <Grid2 size={{ xs: 12, md: 4 }}>
                                                    <Card sx={{ boxShadow: "none", borderRadius: '4px', bgcolor: '#F3F3F3' }}>
                                                        <CardHeader
                                                            avatar={
                                                                <Avatar sx={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 500,
                                                                    lineHeight: "24px",
                                                                    letterSpacing: '-0.4px',
                                                                    bgcolor: '#415385'
                                                                }} aria-label="recipe">
                                                                    {(item?.project_manager_name || '')
                                                                        .split(' ')
                                                                        .map(word => word.charAt(0).toUpperCase())
                                                                        .join('')
                                                                        .slice(0, 2)}
                                                                </Avatar>
                                                            }
                                                            sx={{
                                                                padding: '10px',
                                                                '& .MuiCardHeader-title': {
                                                                    fontSize: '16px',
                                                                    fontWeight: 600,
                                                                    lineHeight: "24px",
                                                                    letterSpacing: '-0.4px',
                                                                    color: '#474747',
                                                                },
                                                                '& .MuiCardHeader-subheader': {
                                                                    fontSize: '14px',
                                                                    fontWeight: 400,
                                                                    lineHeight: "24px",
                                                                    letterSpacing: '-0.4px',
                                                                    color: '#696969',
                                                                }
                                                            }}
                                                            title={item?.project_manager_name ?? "-"}
                                                            subheader={"Project Manager"}
                                                        />
                                                    </Card>
                                                </Grid2>
                                            </Stack>
                                        </Grid2>
                                    </Grid2>
                                    <Grid2 rowSpacing={1} columnSpacing={1} container sx={{ mt: 2 }}>
                                        <Grid2 size={{ xs: 12, md: 2.5 }}>
                                            <PhysicalProgress projectId={item?.project_id} />
                                            <FinancialProgress data={
                                                [
                                                    {
                                                        name: "Overall",
                                                        data: [{ y: 100, color: "#E9E3E3", radius: "90%", innerRadius: "100%" }]
                                                    },
                                                    {
                                                        name: "Plan",
                                                        data: [{ y: 83.9, color: "#295477", radius: "75%", innerRadius: "85%" }]
                                                    },
                                                    {
                                                        name: "Actual",
                                                        data: [{ y: 83.5, color: "#006045", radius: "60%", innerRadius: "70%" }]
                                                    },
                                                ]
                                            } />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 2.5 }}>
                                            <SchedulePerformanceIndicator id={item?.project_id} />

                                            <CostPerformanceIndicator />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 4 }}>
                                            <MonteCarloSimulationReport projectId={item?.project_id} />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 3 }}>
                                            <MonthlyHighlights />
                                        </Grid2>
                                    </Grid2>
                                </Box>
                            </Grid2>
                        )
                    })
                ) : (
                    <Typography sx={{ padding: 2, textAlign: 'center', fontSize: "18px", lineHeight: "100%", fontWeight: 500, color: "#999" }}>
                        No search result found
                    </Typography>
                )
                }
            </Grid2 >
        </>
    );
};

export default ProgramDashboard;